/**
 * Role-based access control config.
 *
 * Edit permissions via the UI at /PermissionsManager (Settings → Permissions).
 * Changes are saved in localStorage and take effect immediately.
 * Use "Reset to Default" in the UI to go back to these defaults.
 *
 * Roles: 'admin' | 'hr' | 'finance' | 'sales'
 */

const ALL   = ['admin', 'hr', 'finance', 'sales']
const STAFF = ['admin', 'hr', 'finance']
const MGMT  = ['admin', 'finance']
const ADMIN = ['admin']

export const defaultPermissions = {
  // ── Dashboard ──────────────────────────────────────────────
  'Dashboard':                    { access: ALL,   edit: ADMIN },

  // ── People ─────────────────────────────────────────────────
  'Tuition Student':              { access: ALL,   edit: ['admin', 'hr'] },
  'TuitionStudentNew':            { access: ALL,   edit: ['admin', 'hr'] },
  'Student':                      { access: ALL,   edit: ['admin', 'hr'] },
  'EditStudent':                  { access: ['admin', 'hr'], edit: ['admin', 'hr'] },
  'Tuition Teacher':              { access: ALL,   edit: ['admin', 'hr'] },
  'Update Teacher':               { access: ['admin', 'hr'], edit: ['admin', 'hr'] },
  'Register Student':             { access: ['admin', 'hr'], edit: ['admin', 'hr'] },
  'RegisterNoStudent':            { access: ALL,   edit: STAFF },

  // ── Academic ───────────────────────────────────────────────
  'Tuition Subject':              { access: MGMT,  edit: ADMIN },
  'Assign Subject':               { access: MGMT,  edit: ADMIN },
  'Tuition Package':              { access: MGMT,  edit: ADMIN },
  'Package':                      { access: MGMT,  edit: ADMIN },
  'Edit Package':                 { access: MGMT,  edit: ADMIN },
  'ManagePackage':                { access: MGMT,  edit: ADMIN },
  'ManageMasterPackage':          { access: MGMT,  edit: ADMIN },
  'TestingMaster':                { access: MGMT,  edit: ADMIN },

  // ── Schedule ───────────────────────────────────────────────
  'Schedule':                     { access: ALL,   edit: ['admin', 'hr'] },
  'Tuition Schedule':             { access: ALL,   edit: ['admin', 'hr'] },
  'ScheduleList':                 { access: ALL,   edit: ['admin', 'hr'] },
  'Calendar':                     { access: ALL,   edit: ['admin', 'hr'] },

  // ── Attendance ─────────────────────────────────────────────
  'Tuition Attendance Student':   { access: ALL,   edit: ['admin', 'hr'] },
  'Tuition Attendance Teacher':   { access: ALL,   edit: ['admin', 'hr'] },
  'Student Attendance':           { access: ALL,   edit: ['admin', 'hr'] },

  // ── Payment ────────────────────────────────────────────────
  'Tuition Payment Student':      { access: MGMT,  edit: MGMT },
  'Tuition Payment Teacher':      { access: MGMT,  edit: MGMT },
  'Direct Payment':               { access: MGMT,  edit: MGMT },
  'Payment Student':              { access: MGMT,  edit: MGMT },
  'Payment Teacher':              { access: MGMT,  edit: MGMT },
  'Payment Receipt':              { access: MGMT,  edit: MGMT },
  'SalarySlip':                   { access: MGMT,  edit: MGMT },
  'UnPaid Transaction':           { access: MGMT,  edit: MGMT },
  'Never Paid Students':          { access: MGMT,  edit: MGMT },
  'Purchase':                     { access: MGMT,  edit: MGMT },
  'PurchaseSummary':              { access: MGMT,  edit: MGMT },
  'Purchase Summary':             { access: MGMT,  edit: MGMT },
  'AssignmentPurchaseStudent':    { access: MGMT,  edit: MGMT },

  // ── Billing ────────────────────────────────────────────────
  'Billing':                      { access: MGMT,  edit: ADMIN },
  'Tuition Registration Fee':     { access: MGMT,  edit: ADMIN },

  // ── E-Pay ──────────────────────────────────────────────────
  'E-Pay Transaction':            { access: MGMT,  edit: MGMT },
  'E-Pay Total':                  { access: MGMT,  edit: MGMT },
  'E-Pay Track':                  { access: MGMT,  edit: MGMT },

  // ── Find Tutor ─────────────────────────────────────────────
  'FindTutor':                    { access: ALL,   edit: STAFF },
  'Find Tutor Exam':              { access: ALL,   edit: STAFF },
  'Tutor List':                   { access: ALL,   edit: STAFF },
  'Msg Analysis':                 { access: STAFF, edit: STAFF },
  'Still Open':                   { access: STAFF, edit: STAFF },
  'Teacher Apply':                { access: STAFF, edit: STAFF },

  // ── Analysis ───────────────────────────────────────────────
  'Analysis':                     { access: MGMT,  edit: ADMIN },
  'Sales Report':                 { access: MGMT,  edit: ADMIN },
  'Product Report':               { access: MGMT,  edit: ADMIN },

  // ── More ───────────────────────────────────────────────────
  'Tuition Broadcast':            { access: STAFF, edit: ADMIN },
  'Tuition Mass Notification':    { access: STAFF, edit: ADMIN },
  'Tuition Mass Notification List': { access: STAFF, edit: ADMIN },
  'CounterVisitor':               { access: STAFF, edit: ADMIN },

  // ── Analysis (full) ────────────────────────────────────────
  'Affiliate Staff':              { access: MGMT,  edit: ADMIN },
  'Affiliate External':           { access: MGMT,  edit: ADMIN },
  'List Affiliate':               { access: MGMT,  edit: ADMIN },

  // ── Attendance (extra) ─────────────────────────────────────
  'Teacher Attended No Student':  { access: ALL,   edit: ['admin', 'hr'] },

  // ── Settings / Staff ───────────────────────────────────────
  'AdminStaff':                   { access: ADMIN, edit: ADMIN },
  'PermissionsManager':           { access: ADMIN, edit: ADMIN },
}

/**
 * Returns permissions merged from defaults + localStorage.
 * Defaults fill in any keys not yet in the saved config,
 * so newly-added protected routes are always enforced.
 */
export function getPermissions() {
  try {
    const stored = localStorage.getItem('adminPermissions')
    if (stored) return { ...defaultPermissions, ...JSON.parse(stored) }
  } catch {}
  return defaultPermissions
}
